import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
  alpha,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google,
  Email,
  Lock,
  Person,
  ArrowBack, // Import ArrowBack
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import LokiBanner from '../assets/Login-bg.jpg'; 

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  
  const isRegisterRoute = location.pathname === '/register' || location.pathname === '/signup';
  const [tabValue, setTabValue] = useState(isRegisterRoute ? 1 : 0);

  useEffect(() => {
    setTabValue(isRegisterRoute ? 1 : 0);
  }, [location.pathname, isRegisterRoute]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)';
  const lightModeGradient = 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)';
  const goldColor = '#C4B66F';
  const darkTextColor = '#051814';

  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const inputSx = {
    '& .MuiInputLabel-root': { color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: isDark ? goldColor : '#0F3B2E' },
    '& .MuiOutlinedInput-root': {
      color: isDark ? 'white' : '#000',
      backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.4)',
      borderRadius: '12px',
      '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' },
      '&.Mui-focused fieldset': { borderColor: isDark ? goldColor : '#0F3B2E' },
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: isDark ? goldColor : '#0F3B2E' },
  };

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '', confirmPassword: '' });

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Logged in successfully!');
      navigate('/profile');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Login failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await register(registerData.username, registerData.email, registerData.password);
      toast.success('Account created successfully!');
      navigate('/profile');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/api/auth/google`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        backgroundImage: `url(${LokiBanner})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: '24px',
            // DARKER BACKGROUND IN LIGHT MODE
            background: isDark 
              ? 'rgba(255, 255, 255, 0.03)' 
              : 'rgba(200, 200, 200, 0.35)', // Darker/Grayish tint for Light mode
            backdropFilter: 'blur(20px)',
            border: isDark 
              ? `1px solid ${alpha(goldColor, 0.2)}` 
              : '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: isDark 
              ? '0 20px 80px rgba(0,0,0,0.6)' 
              : '0 20px 60px rgba(0,0,0,0.15)',
            width: '100%',
            position: 'relative' // Needed for absolute positioning of back button
          }}
        >
          {/* BACK BUTTON */}
          <IconButton 
            onClick={() => navigate(-1)}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              color: isDark ? 'rgba(255,255,255,0.7)' : darkTextColor,
              '&:hover': {
                color: isDark ? goldColor : '#000',
                bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <ArrowBack />
          </IconButton>

          <Box textAlign="center" mb={4} mt={2}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom 
              sx={{ 
                ...discordFont, 
                color: isDark ? 'white' : darkTextColor,
                fontSize: { xs: '1.75rem', md: '2.25rem' } 
              }}
            >
              {tabValue === 0 ? 'Welcome Back' : 'Join the Community'}
            </Typography>
            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
              {tabValue === 0 ? 'Sign in to access your workspace' : 'Start building your network today'}
            </Typography>
          </Box>

          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            centered
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': { backgroundColor: isDark ? goldColor : '#0F3B2E' },
              '& .MuiTab-root': {
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1rem',
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                '&.Mui-selected': { color: isDark ? 'white' : darkTextColor },
              },
            }}
          >
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
          </Tabs>

          <Box sx={{ mb: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<Google />}
              onClick={handleGoogleLogin}
              sx={{
                py: 1.5,
                borderRadius: '12px',
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                color: isDark ? 'white' : '#000',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: isDark ? goldColor : '#0F3B2E',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#fff',
                }
              }}
            >
              Continue with Google
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
            <Divider sx={{ flex: 1, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
            <Typography sx={{ mx: 2, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', fontSize: '0.9rem' }}>
              or continue with email
            </Typography>
            <Divider sx={{ flex: 1, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
          </Box>

          <TabPanel value={tabValue} index={0}>
            <form onSubmit={handleLoginSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.8,
                    borderRadius: '100px',
                    background: isDark ? lokiGradient : lightModeGradient,
                    color: isDark ? goldColor : 'white',
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    border: isDark ? `1px solid ${alpha(goldColor, 0.2)}` : 'none',
                    ...discordFont,
                    '&:hover': {
                      filter: 'brightness(1.1)',
                      boxShadow: `0 0 20px ${alpha(goldColor, 0.3)}`,
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </Stack>
            </form>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <form onSubmit={handleRegisterSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={registerData.username}
                  onChange={handleRegisterChange}
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={registerData.password}
                  onChange={handleRegisterChange}
                  required
                  helperText={
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                      At least 8 chars with number & special char
                    </Typography>
                  }
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={registerData.confirmPassword}
                  onChange={handleRegisterChange}
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.8,
                    borderRadius: '100px',
                    background: isDark ? lokiGradient : lightModeGradient,
                    color: isDark ? goldColor : 'white',
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    border: isDark ? `1px solid ${alpha(goldColor, 0.2)}` : 'none',
                    ...discordFont,
                    '&:hover': {
                      filter: 'brightness(1.1)',
                      boxShadow: `0 0 20px ${alpha(goldColor, 0.3)}`,
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                </Button>
              </Stack>
            </form>
          </TabPanel>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;